import { css } from '@emotion/react';
import { useAtom } from 'jotai';
import * as React from 'react';

import { CategoryOption, Choice } from 'api/api';

import { CategoryAddressProvider } from '~src/address/search-address/address-provider';
import { useCategoryState } from '~src/category/category-state';
import { InlineExtensionList } from '~src/category/inline-extension-list';
import { openedAtom } from '~src/category/state/category-extesion-atoms';
import { constructUserChoice } from '~src/category/user-choice';
import { UserChoiceProvider } from '~src/category/user-choice-provider';
import { OpenListButtonLayout } from '~src/commons/components/button/open-list-button';
import { LogoDeck } from '~src/commons/components/logo-deck-row';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { DeliveryTime } from '~src/delivery-time/delivery-time';
import { PriceComponent } from '~src/price/price';
import { colors } from '~src/theme/colors';
import { useAriaGridCell } from '~src/ui/aria-grid';
import { ListState, useInlineListState } from '~src/ui/list-state';
import { ShippingCardUI } from '~src/ui/shipping-card';
import { useIpc } from '~src/utils/ipc-utils';

import { useHasAddress } from '~address-form/utils/address-utils';

interface InlineHomeDeliveryListItemProps {
  option: CategoryOption;
  listState: ListState<CategoryOption>;
  onSelect: (choice: Choice) => void;
}

export const InlineHomeDeliveryListItem: React.FC<InlineHomeDeliveryListItemProps> = ({
  option,
  listState,
  onSelect,
}) => {
  const { tICU } = useTranslations();
  const category = useCategoryState();

  const ref = React.useRef(null);

  const { cellProps, isSelected } = useAriaGridCell({
    itemKey: option.shipping_method,
    listState,
    ref,
    enableAutoSelect: false,
    onSelect: itemKey => {
      // don't submit the same location
      if (itemKey === listState.selectedKey) return;

      onSelect(constructUserChoice(category, option));
    },
  });

  return (
    <ShippingCardUI
      headerExtensions={{
        price: <PriceComponent value={option.price} />,
        logo: <LogoDeck shippingMethods={[option.carrier_logo || option.shipping_method]} />,
        subtitle: <DeliveryTime deliveryTime={option.delivery_times[0]} type="delivery" />,
      }}
      radioTitle={
        isSelected ? tICU(`widget.aria.SELECTED_HOME_DELIVERY`) : tICU(`widget.aria.HOME_DELIVERY`)
      }
      key={option.shipping_method}
      selected={isSelected}
      title={option.carrier}
      reserveLogoSpace={category.flags.show_carrier_logos}
      spacing="normal"
      textStyle="normal"
      enablePadding
      ariaLevel={4}
      innerRef={ref}
      {...cellProps}
    />
  );
};

interface InlineHomeDevlieryExtensionProps {
  initialSelectedOption: CategoryOption;
}

export const InlineHomeDevlieryExtension: React.FC<InlineHomeDevlieryExtensionProps> = ({
  initialSelectedOption,
}) => {
  const ipc = useIpc();
  const { tICU } = useTranslations();
  const category = useCategoryState();
  const hasAddress = useHasAddress();
  const listState = useInlineListState({
    items: category.options,
    getKey: (option: CategoryOption) => option.shipping_method,
    defaultSelectedKey: initialSelectedOption.shipping_method,
  });

  const [, setOpened] = useAtom(openedAtom);

  const buttonRef = React.useRef<HTMLDivElement>(null);

  return (
    <CategoryAddressProvider>
      {({ onOpen }) => (
        <UserChoiceProvider>
          {({ onUpdate }) => (
            <OpenListButtonLayout
              innerRef={buttonRef}
              onClick={() => (hasAddress ? setOpened(prev => !prev) : onOpen())}
              title={
                hasAddress
                  ? { text: tICU('widget.DELIVERY_BY'), textStyle: 'normal', color: colors.black }
                  : undefined
              }
              description={{
                text: hasAddress ? initialSelectedOption.carrier : tICU('widget.SELECT_CARRIER'),
                textStyle: 'normal',
                color: colors.darkGrey,
              }}
              textSize={hasAddress ? 'normal' : 'small'}
            >
              <InlineExtensionList
                getId={item => item.shipping_method}
                listState={listState}
                listStyles={css({
                  '&>[role=grid]>[role=row]:first-child>[role=gridcell]': {
                    borderTop: 'none',
                    paddingTop: '13px',
                  },
                })}
              >
                {({ item, hidden }) =>
                  hidden || (
                    <InlineHomeDeliveryListItem
                      key={item.shipping_method}
                      option={item}
                      listState={listState}
                      onSelect={newChoice => {
                        onUpdate(newChoice);
                        setOpened(false);
                        if (buttonRef.current) {
                          ipc.send({
                            name: 'request_scroll_change',
                            data: buttonRef.current.getBoundingClientRect().top,
                          });
                        }
                      }}
                    />
                  )
                }
              </InlineExtensionList>
            </OpenListButtonLayout>
          )}
        </UserChoiceProvider>
      )}
    </CategoryAddressProvider>
  );
};
